import { Box, CircularProgress, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { Navigate, Outlet, useSearchParams } from 'react-router-dom';

import { HomeHQPaywall } from '@/components/billing/HomeHQPaywall';
import { usePropertiesContext } from '@/context/PropertiesProvider';
import { useClientGroups } from '@/hooks/useClientGroups';
import { useHomeHQAccess } from '@/hooks/useHomeHQAccess';
import { useUserRole } from '@/hooks/useUserRole';

/** Give the Stripe webhook this long after Checkout before showing the paywall again. */
export const CHECKOUT_WAIT_MS = 60_000;

export const HomeHQProtectedRoute = () => {
  const { canAccessAdmin, isAdvisor } = useUserRole();
  const { hasOwnedProperties, isInitialFetchingProperties } = usePropertiesContext();
  const { groups, isLoading: isLoadingGroups } = useClientGroups();
  const isInternal = canAccessAdmin || isAdvisor;

  // Right after Stripe Checkout the webhook may not have landed yet — keep
  // re-checking instead of flashing the paywall at someone who just paid.
  const [searchParams] = useSearchParams();
  const [checkoutWaitExpired, setCheckoutWaitExpired] = useState(false);
  const justCheckedOut = searchParams.get('checkout') === 'success' && !checkoutWaitExpired;
  useEffect(() => {
    if (searchParams.get('checkout') !== 'success') return;
    const timeoutId = setTimeout(() => setCheckoutWaitExpired(true), CHECKOUT_WAIT_MS);
    return () => clearTimeout(timeoutId);
  }, [searchParams]);
  const { data: access, isLoading: isLoadingAccess } = useHomeHQAccess({
    enabled: !isInternal,
    pollWhileLocked: justCheckedOut,
  });

  // Admins/super-admins and Advisors always get access (they view client HomeHQ)
  if (isInternal) return <Outlet />;

  // Wait for properties and group memberships to load before deciding
  if (isInitialFetchingProperties || isLoadingGroups) return null;

  // Advisor-managed buyers with no owned property → back to summary
  if (!hasOwnedProperties && groups.length > 0) {
    return <Navigate to="/dashboard/summary" replace />;
  }

  // Owners, household members, and self-serve users need live HomeHQ access.
  // Self-serve users choose a plan here first; the onboarding modal (claim a
  // home, upload a report) runs once they're subscribed.
  if (isLoadingAccess || !access) return null;
  if (access.hasAccess) return <Outlet />;

  if (justCheckedOut) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, mt: 10 }}>
        <CircularProgress size={28} sx={{ color: '#bbb7af' }} />
        <Typography sx={{ color: '#f3f3f3' }}>Finishing setting up your Home HQ…</Typography>
      </Box>
    );
  }

  return <HomeHQPaywall access={access} />;
};
